import { ModalOpenButton, useScreenWidth } from "@msys/ui";
import { Backdrop, Box, Stack, useTheme } from "@mui/material";
import { SxProps, SystemCssProperties } from "@mui/system";
import { useFormik } from "formik";
import { isEqual } from "lodash-es";
import React from "react";
import { BackButton } from "../../commons/button/BackButton.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import { FilterButton } from "../../commons/filters/FilterButton.js";
import { useAutoSave } from "../../commons/hooks/useAutoSave.js";
import { CollectionView } from "../../commons/hooks/useCollectionView.js";
import { FavouritesIconButton } from "./filters/FavouritesIconButton.js";
import {
  canShowChips,
  ProductFilterChips,
} from "./filters/ProductFilterChips.js";
import {
  getProductFormValues,
  productFiltersDefaultValue,
  ProductFiltersFormValues,
  productFiltersValidationSchema,
} from "./filters/ProductFilterFields.js";
import { ProductFilters } from "./filters/ProductFilters.js";
import {
  ProductSearchType,
  ProductSearchTypeOption,
} from "./filters/useProductSearchTypes.js";
import {
  ProductSearchInputWithManualForm,
  SearchInputFormValues,
} from "./inputs/ProductSearchInput.js";
import { ProductFilterModal } from "./modals/ProductFilterModal.js";
import { ProductSearchItem__ProductSearchResultFragment } from "./Product.generated.js";

export type ChildrenFn = (props: {
  SearchLine: React.ReactNode;
  FiltersLine: React.ReactNode;
  ChipsLine: React.ReactNode;
  SearchBackdrop: React.ReactNode;
}) => JSX.Element;

export function ProductSearchListHeader({
  initialFilterState,
  filters,
  setFilters,
  resetFilters,
  productSearchTypeOptions,
  productSearchType,
  setProductSearchType,
  searchTerm,
  setSearchTerm,
  activeView,
  setActiveView,
  productSearchVariables,
  canChangeProductType,
  hideFilters,
  searchEnabled,
  setSearchEnabled,
  allowedViews,
  ActionButtons,
  AdditionalChips,
  handleBackClick,
  handleSearchSubmit,
  onProductSelect,
  // spacing props, to support both in page and in modal
  px = 2,
  mx = -2,
  pt = 2,
  mt = -2,
  pb = 1,
  mb = -1,
  // rendering lines
  children,
}: {
  initialFilterState?: ProductFiltersFormValues;
  filters: ProductFiltersFormValues;
  setFilters: React.Dispatch<React.SetStateAction<ProductFiltersFormValues>>;
  resetFilters: () => void;
  productSearchTypeOptions: ProductSearchTypeOption[];
  productSearchType: ProductSearchType;
  setProductSearchType: (value: ProductSearchType) => void;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  activeView: CollectionView;
  setActiveView: (newValue: CollectionView) => void;
  productSearchVariables: { filters: ProductFiltersFormValues };
  canChangeProductType?: boolean;
  hideFilters?: boolean;
  searchEnabled: boolean;
  setSearchEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  allowedViews: CollectionView[];
  ActionButtons?: React.ReactNode;
  AdditionalChips?: React.ReactNode;
  handleBackClick?(): void;
  handleSearchSubmit?(values: SearchInputFormValues): Promise<void> | void;
  onProductSelect?: (
    product: ProductSearchItem__ProductSearchResultFragment
  ) => void;
  // spacing props, to support both in page and in modal
  px?: SystemCssProperties["px"];
  mx?: SystemCssProperties["mx"];
  pt?: SystemCssProperties["pt"];
  mt?: SystemCssProperties["mt"];
  pb?: SystemCssProperties["pb"];
  mb?: SystemCssProperties["mb"];
  // rendering lines
  children: ChildrenFn;
}) {
  const { isMinTablet } = useScreenWidth();

  const initialValues = React.useMemo((): ProductFiltersFormValues => {
    return {
      ...productFiltersDefaultValue,
      ...initialFilterState,
      ...filters,
    };
  }, [filters, initialFilterState]);

  const formikProps = useFormik<ProductFiltersFormValues>({
    initialValues,
    validationSchema: productFiltersValidationSchema,
    validateOnMount: true,
    onSubmit: async (newValues, formikHelpers) => {
      setFilters(value => ({
        ...value,
        ...newValues,
      }));
    },
  });

  useAutoSave({
    enableReinitialize: true,
    initialValues,
    formikProps,
  });

  const onReset =
    canShowChips(filters) &&
    (!initialFilterState ||
      !isEqual(
        getProductFormValues(formikProps.values),
        getProductFormValues(initialFilterState)
      ))
      ? () => {
          resetFilters();
          formikProps.resetForm({
            values: { ...productFiltersDefaultValue, ...initialFilterState },
          });
        }
      : undefined;

  const theme = useTheme();

  const SearchField = (
    <ProductSearchInputWithManualForm
      productSearchTypeOptions={productSearchTypeOptions}
      productSearchType={productSearchType}
      setProductSearchType={setProductSearchType}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      searchEnabled={searchEnabled}
      setSearchEnabled={setSearchEnabled}
      handleSubmit={handleSearchSubmit}
      filters={filters}
      setFilters={setFilters}
      onProductSelect={onProductSelect}
    />
  );
  const SwitchViewButton = !hideFilters ? (
    <SwitchCollectionViewButton
      allowedViews={allowedViews}
      activeView={activeView}
      setActiveView={setActiveView}
    />
  ) : undefined;
  const FilterButtonComponent = !hideFilters ? (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        height: "32px",
        alignItems: "center",
      }}
    >
      <ModalOpenButton
        Modal={ProductFilterModal}
        modalProps={{
          formikProps,
          productSearchVariables,
          canChangeProductType,
          onReset,
        }}
      >
        <FilterButton />
      </ModalOpenButton>
      <FavouritesIconButton
        isActive={filters.organisationFavourite === true}
        toggleActive={active => {
          formikProps.setFieldValue(
            "organisationFavourite",
            active ? true : null
          );
          formikProps.submitForm();
        }}
      />
    </Stack>
  ) : undefined;
  const InlineFilters =
    isMinTablet && !hideFilters ? (
      <ProductFilters
        formikProps={formikProps}
        productSearchVariables={productSearchVariables}
        canChangeProductType={canChangeProductType}
        viewType="buttons"
      />
    ) : undefined;
  const FilterChips =
    canShowChips(filters) && !hideFilters ? (
      <ProductFilterChips
        filters={filters}
        setFilters={setFilters}
        onReset={onReset}
        canChangeProductType={canChangeProductType}
        AdditionalChips={AdditionalChips}
      />
    ) : undefined;
  const backButton =
    handleBackClick && !hideFilters ? (
      <BackButton onClick={handleBackClick} />
    ) : undefined;

  // to support backdrop when search input is focused
  // @ts-ignore
  const style: SxProps = {
    position: searchEnabled ? "absolute" : "relative",
    zIndex: 2,
    px: px,
    mx: mx,
    pb: pb,
    mb: mb,
    pt: pt,
    mt: mt,
    width: "100%",
    boxSizing: searchEnabled ? "border-box" : "content-box",
    backgroundColor: theme.palette.common.white,
  };

  const SearchLine = !isMinTablet ? (
    // mobile
    <>
      <Box
        sx={[
          {
            p: 1,
          },
          ...(Array.isArray(style) ? style : [style]),
        ]}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            width: "100%",
            alignItems: "center",
            position: "relative",
            height: "30px",
          }}
        >
          {!searchEnabled && backButton && (
            <Box
              sx={{
                height: "30px",
                display: "flex",
              }}
            >
              {backButton}
            </Box>
          )}
          {SearchField}
          {!searchEnabled && FilterButtonComponent}
          {!searchEnabled && SwitchViewButton}
          {!searchEnabled && ActionButtons && (
            <Box
              sx={{
                height: "30px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {ActionButtons}
            </Box>
          )}
        </Stack>
      </Box>
      {searchEnabled && (
        /*placeholder*/ <Box
          sx={{
            height: "30px",
            margin: "0 !important",
          }}
        />
      )}
    </>
  ) : (
    // desktop
    <>
      <Box sx={style}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            width: "100%",
            alignItems: "center",
            position: "relative",
            height: "30px",
          }}
        >
          {!searchEnabled && backButton && (
            <Box
              sx={{
                height: "30px",
                display: "flex",
              }}
            >
              {backButton}
            </Box>
          )}
          {SearchField}
          {!searchEnabled && ActionButtons && (
            <Box
              sx={{
                height: "30px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {ActionButtons}
            </Box>
          )}
        </Stack>
      </Box>
      {searchEnabled && (
        /*placeholder*/ <Box
          sx={{
            height: "30px",
            margin: "0 !important",
          }}
        />
      )}
    </>
  );

  const FiltersLine = !isMinTablet ? null : InlineFilters ||
    FilterButtonComponent ||
    SwitchViewButton ? (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        width: "100%",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          flex: 1,
          alignItems: "flex-start",
        }}
      >
        {InlineFilters}
        {FilterButtonComponent}
      </Stack>
      {SwitchViewButton}
    </Stack>
  ) : null;

  const ChipsLine = FilterChips;

  const SearchBackdrop = <Backdrop open={searchEnabled} sx={{ zIndex: 1 }} />;

  return children({ SearchLine, FiltersLine, ChipsLine, SearchBackdrop });

  // default
  /*
  return (
    <Stack direction="column" width="100%" spacing={1}>
      {SearchLine}
      {FiltersLine}
      {ChipsLine}
    </Stack>
  );
  */
}
